import { createReducer } from 'src/helpers/reducer';
import actionTypes from './actionTypes';
import type { IRateCardsState, ILoadDataSuccessPayload, IRateCardEngineOption } from './types';

const initState: IRateCardsState = {
  data: null,
  filters: [],
  sortableColumns: [],
  total: 0,
  filteredTotal: 0,
  loading: false,
  error: false,
  engines: null,
  formDataError: false,
  deleting: false,
  deleteFailed: false,
};

function onLoadRequest(state: IRateCardsState): IRateCardsState {
  return {
    ...state,
    loading: true,
    error: false,
  };
}

function onLoadSuccess(state: IRateCardsState, { payload }: { payload: ILoadDataSuccessPayload }): IRateCardsState {
  return {
    ...state,
    data: payload.records,
    filters: payload.filters,
    sortableColumns: payload.sortableColumns,
    total: payload.total,
    filteredTotal: payload.filteredTotal,
    loading: false,
    error: false,
  };
}

// `data` survives so a failed refetch does not blank a table that was already showing rows;
// `error` is what stops the page rendering the empty state.
function onLoadError(state: IRateCardsState): IRateCardsState {
  return {
    ...state,
    loading: false,
    error: true,
  };
}

export default createReducer(initState, {
  // Back to unknown: `null` is what the pages render a skeleton for, and a retry after a failure
  // has to leave that state rather than sitting on the previous empty answer.
  [actionTypes.LOAD_FORM_DATA_REQUEST]: (state: IRateCardsState): IRateCardsState => ({
    ...state,
    engines: null,
    formDataError: false,
  }),

  [actionTypes.LOAD_FORM_DATA_SUCCESS]: (
    state: IRateCardsState,
    { payload }: { payload: { engines: IRateCardEngineOption[] } },
  ): IRateCardsState => ({
    ...state,
    engines: payload.engines,
    formDataError: false,
  }),

  [actionTypes.LOAD_FORM_DATA_ERROR]: (state: IRateCardsState): IRateCardsState => ({
    ...state,
    engines: [],
    formDataError: true,
  }),

  [actionTypes.LOAD_DATA_REQUEST]: onLoadRequest,
  [actionTypes.LOAD_DATA_SUCCESS]: onLoadSuccess,
  [actionTypes.LOAD_DATA_ERROR]: onLoadError,

  // Cleared at both ends: without it here a refused delete followed by a successful one would still read
  // as failed and skip the refetch; without it on success the flag outlives the attempt it describes.
  [actionTypes.DELETE_REQUEST]: (state: IRateCardsState): IRateCardsState => ({
    ...state,
    deleting: true,
    deleteFailed: false,
  }),

  [actionTypes.DELETE_SUCCESS]: (state: IRateCardsState): IRateCardsState => ({
    ...state,
    deleting: false,
    deleteFailed: false,
  }),

  [actionTypes.DELETE_ERROR]: (state: IRateCardsState): IRateCardsState => ({
    ...state,
    deleting: false,
    deleteFailed: true,
  }),

  [actionTypes.CLEAR_STORE]: () => initState,
});
